import type { Item, UnknownName, UnknownNameAssigned, UnknownNameSuggestion } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api } from '../api';
import { ItemPicker } from '../components/ItemPicker';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const keyOf = (u: Pick<UnknownName, 'source' | 'name'>) => `${u.source}:${u.name}`;

/** Names on invoices, the register and vendor lists that are not an item yet: tap one, pick the item. */
export function InboxPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [picking, setPicking] = useState<UnknownName | null>(null);
  const [done, setDone] = useState<(UnknownNameAssigned & { itemName: string }) | null>(null);
  const list = useQuery({ queryKey: qk.unknownNames, queryFn: ({ signal }) => api.unknownNames(signal) });
  const assign = useMutation({
    mutationFn: (v: { unknown: UnknownName; item: Item }) => api.assignUnknownName({ source: v.unknown.source, name: v.unknown.name, itemId: v.item.id }).then((r) => ({ ...r, itemName: v.item.name })),
    onSuccess: (result) => {
      setPicking(null);
      setDone(result);
      void qc.invalidateQueries({ queryKey: qk.unknownNames });
      void qc.invalidateQueries({ queryKey: ['items'] });
    },
  });

  return (
    <div className="stack">
      <PageHead title={t('inbox')} back="/items" />
      <p className="muted">{t('inboxHint')}</p>
      {done ? (
        <div className="flash" role="status">
          {t('assigned')}: {done.name} → {done.itemName} · {done.linesAssigned} {t('lines')}
        </div>
      ) : null}
      {assign.error ? <ErrorBox error={assign.error} /> : null}
      {list.data && list.data.length > 0 ? <SuggestMatches names={list.data} /> : null}
      {picking ? (
        <section className="stack-sm">
          <h2>
            {t('assignTo')} “{picking.name}”?
          </h2>
          <ItemPicker onPick={(item) => assign.mutate({ unknown: picking, item })} onCancel={() => setPicking(null)} busy={assign.isPending} />
        </section>
      ) : null}
      <QueryView query={list}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('inboxEmpty')}</Empty>
          ) : (
            <ul className="list">
              {rows.map((u) => (
                <li key={keyOf(u)}>
                  <button type="button" className="row" aria-pressed={picking ? keyOf(picking) === keyOf(u) : false} onClick={() => setPicking(u)}>
                    <span className="row-main">
                      <span className="row-title">{u.name}</span>
                      <span className="row-sub">
                        <span className="badge">{t(`source_${u.source}`)}</span>
                        {u.lineCount} {t('lines')}
                        {u.lastSeen ? ` · ${formatDate(u.lastSeen, intlLocale, 'date')}` : ''}
                      </span>
                    </span>
                    {u.amount != null ? <span className="row-end-value">{formatMoney(u.amount, intlLocale)}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}

/** The inbox in chunks small enough for one answer each (about a minute with Claude). */
const CHUNK = 60;

/**
 * Suggest matches (decision 134): the same words first (units and counts set aside), then Claude for the rest. The
 * office ticks what is right and links them in one go; nothing is linked by the app itself (AI proposes, people decide).
 */
function SuggestMatches({ names }: { names: UnknownName[] }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [rows, setRows] = useState<(UnknownNameSuggestion & { checked: boolean })[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [linking, setLinking] = useState<{ done: number; total: number } | null>(null);
  const [linked, setLinked] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const stop = useRef(false);

  async function suggest() {
    stop.current = false;
    setRows([]);
    setLinked(null);
    setError(null);
    setAiNote(null);
    const todo = names.map((n) => ({ source: n.source, name: n.name }));
    setProgress({ done: 0, total: todo.length });
    try {
      for (let i = 0; i < todo.length && !stop.current; i += CHUNK) {
        const r = await api.suggestUnknownNames({ names: todo.slice(i, i + CHUNK), ai: true });
        if (r.aiError) setAiNote(r.aiError);
        // a sure match is ticked; a likely one waits for a look
        setRows((cur) => [...cur, ...r.suggestions.map((s) => ({ ...s, checked: s.confidence === 'sure' }))]);
        setProgress({ done: Math.min(todo.length, i + CHUNK), total: todo.length });
      }
    } catch (e) {
      setError(e);
    } finally {
      setProgress(null);
    }
  }

  async function link() {
    const chosen = rows.filter((r) => r.checked);
    stop.current = false;
    setError(null);
    setLinking({ done: 0, total: chosen.length });
    let n = 0;
    try {
      for (const r of chosen) {
        if (stop.current) break;
        await api.assignUnknownName({ source: r.source, name: r.name, itemId: r.itemId });
        n += 1;
        setLinking({ done: n, total: chosen.length });
      }
    } catch (e) {
      setError(e);
    } finally {
      setLinking(null);
      setLinked(n);
      const gone = new Set(chosen.slice(0, n).map((r) => keyOf(r)));
      setRows((cur) => cur.filter((r) => !gone.has(keyOf(r))));
      void qc.invalidateQueries({ queryKey: qk.unknownNames });
      void qc.invalidateQueries({ queryKey: ['items'] });
    }
  }

  const checked = rows.filter((r) => r.checked).length;
  const busy = progress !== null || linking !== null;
  return (
    <section className="card stack-sm" data-testid="suggest-matches" style={{ maxWidth: 'none' }}>
      <div className="picker-head" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="btn btn-primary" onClick={() => void suggest()} disabled={busy}>
          {t('suggestMatches')}
        </button>
        {busy ? (
          <button type="button" className="btn btn-secondary" onClick={() => (stop.current = true)}>
            {t('stop')}
          </button>
        ) : null}
        {progress ? (
          <span className="muted" role="status">
            {t('matchingProgress').replace('{done}', String(progress.done)).replace('{total}', String(progress.total))}
          </span>
        ) : null}
        {linking ? (
          <span className="muted" role="status">
            {t('linkingProgress').replace('{done}', String(linking.done)).replace('{total}', String(linking.total))}
          </span>
        ) : null}
        {linked !== null && !busy ? (
          <span className="flash" role="status">
            {t('linkedN').replace('{n}', String(linked))}
          </span>
        ) : null}
      </div>
      <p className="muted">{t('suggestMatchesHint')}</p>
      {aiNote ? <p className="field-error">{t('suggestAiFailed')} ({aiNote})</p> : null}
      {error ? <ErrorBox error={error} /> : null}
      {rows.length > 0 ? (
        <>
          <div className="picker-head" style={{ flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={() => void link()} disabled={busy || checked === 0}>
              {t('linkChecked').replace('{n}', String(checked))}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRows((cur) => cur.map((r) => ({ ...r, checked: true })))} disabled={busy}>
              {t('checkAll')}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRows((cur) => cur.map((r) => ({ ...r, checked: false })))} disabled={busy}>
              {t('checkNone')}
            </button>
          </div>
          <div className="table-wrap">
            <table className="data-table" aria-label={t('suggestMatches')}>
              <thead>
                <tr>
                  <th />
                  <th>{t('nameOnDocs')}</th>
                  <th>{t('sourceCol')}</th>
                  <th>{t('productService')}</th>
                  <th>{t('matchHow')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={keyOf(r)}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`${t('link')} ${r.name}`}
                        checked={r.checked}
                        disabled={busy}
                        onChange={(e) => setRows((cur) => cur.map((x) => (keyOf(x) === keyOf(r) ? { ...x, checked: e.target.checked } : x)))}
                      />
                    </td>
                    <td>{r.name}</td>
                    <td>
                      <span className="badge">{t(`source_${r.source}`)}</span>
                    </td>
                    <td>
                      <strong>{r.itemName}</strong>
                    </td>
                    <td>
                      <span className={r.confidence === 'sure' ? 'badge' : 'badge badge-warn'}>{t(r.by === 'words' ? 'matchByWords' : r.confidence === 'sure' ? 'matchClaudeSure' : 'matchClaudeLikely')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
